import { getGetFlightsUrl } from 'generatedTypes/flight/services/flight-controller';
import { useState } from 'react';
import { sortParamSerializer } from 'utils/urlUtil';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type {
    FetchFlights,
    FlightResponse,
    GetFlightsParams,
    PagedResponseFlightResponse
} from './types';

const useFlights = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchFlights: FetchFlights = async requestOptions => {
        try {
            setIsLoading(true);
            const {
                applicationId,
                includeArchived,
                pageSize = DEFAULT_PAGE_SIZE,
                pageToken,
                sort,
                startTimeFrom,
                startTimeTo
            } = requestOptions || {};

            const baseUrl = getGetFlightsUrl({
                applicationId,
                includeArchived,
                pageSize,
                pageToken,
                startTimeFrom,
                startTimeTo
            });
            const sortQuery = sortParamSerializer(sort);
            const url = sortQuery
                ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${sortQuery}`
                : baseUrl;

            return await withRetry(
                () => authenticatedGet<PagedResponseFlightResponse>(url),
                CONNECTION_ERROR,
                () => fetchFlights(requestOptions)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data,
        fetch: paginatedFetch,
        reset
    } = usePagination<FlightResponse>({
        callback: fetchFlights
    });

    const handleFetch = (args?: GetFlightsParams) => {
        const {
            applicationId,
            includeArchived,
            pageSize = DEFAULT_PAGE_SIZE,
            sort,
            startTimeFrom,
            startTimeTo
        } = args || {};

        return paginatedFetch({
            applicationId,
            includeArchived,
            pageSize,
            sort,
            startTimeFrom,
            startTimeTo
        });
    };

    return {
        fetch: handleFetch,
        flights: data as FlightResponse[],
        isLoading,
        reset
    };
};

export default useFlights;
